import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import MasterModalContext from 'contexts/MasterModalContext'
import {
  get as getLocalStorage,

  set as setLocalStorage,
  remove as removeLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { matchPath } from 'react-router'
import clearLoyaltyEnrolment from 'actions/loyalty/clearLoyaltyEnrolment'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import { diffInDays } from 'lib/datetime/dateUtils'
import useLoyaltyUserEligibilityTier from './useLoyaltyUserEligibilityTier'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { DISMISS_JOIN_MODAL_STORAGE_KEY } from 'loyalty/constants/loyaltyConfig'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { isLuxLoyaltyEnabledInAgentHub } from 'agentHub/selectors/agentHubSelectors'

const pathsBlacklist = [
  '/:region/checkout',
  '/:region/booking/confirmation',
  `/:region/${LOYALTY_LANDING_PAGE}`,
  '/:region/offer',
  '/:region/account/communication-preferences/unsubscribe',
]

function useLoyaltyEnrolExistingUserModal() {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const modalState = useContext(MasterModalContext)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const loyaltyAccount = useAppSelector(state => state.loyalty.account)
  const [eligibilityTier, eligibilityFetching, eligibilityError] = useLoyaltyUserEligibilityTier()
  const modalDismissalDate = getLocalStorage(DISMISS_JOIN_MODAL_STORAGE_KEY)

  const isDismissedPeriod = useMemo(() => modalDismissalDate ? diffInDays(new Date(modalDismissalDate), new Date()) < 14 : false, [modalDismissalDate])

  const path = useAppSelector(state => state.router.location.pathname)

  const isModalPreventedPage = !!matchPath(path, {
    path: pathsBlacklist,
    exact: false,
  })

  const shouldShowModal = loggedIn &&
    loyaltyEnabled &&
    !isDismissedPeriod &&
    !isLuxLoyaltyEnabledInAgentHub &&
    !modalState.open &&
    !hasLoyaltyAccount &&
    !loyaltyAccount.fetching &&
    !loyaltyAccount.error &&
    !isModalPreventedPage &&
    !eligibilityFetching &&
    !eligibilityError &&
    !!eligibilityTier

  const handleClearEnrolment = useCallback(() => {
    dispatch(clearLoyaltyEnrolment())
    setLocalStorage(DISMISS_JOIN_MODAL_STORAGE_KEY, new Date().toISOString())
  }, [dispatch])

  useEffect(() => {
    if (modalDismissalDate && !isDismissedPeriod) {
      removeLocalStorage(DISMISS_JOIN_MODAL_STORAGE_KEY)
    }
  }, [isDismissedPeriod, modalDismissalDate])

  const activateMembershipCta = intl.formatMessage({
    id: 'ZQlZxOs_CeHcomZKCni_',
    defaultMessage: 'Activate free membership',
    description: 'Primary button text on the loyalty join modal shown to existing users',
  })

  const maybeLaterCta = intl.formatMessage({
    id: '7ZOFYx1kmzTmLpkOGoln',
    defaultMessage: 'Maybe later',
    description: 'Secondary dismiss button text on the loyalty join modal shown to existing users',
  })

  const showJoinModal = useCallback(async() => {
    const joinConfirmed = await showModal<boolean>(<LoyaltyJoinModal
      onAfterClose={handleClearEnrolment}
      primaryActionCta={activateMembershipCta}
      secondaryActionCta={maybeLaterCta}
    />)

    setLocalStorage(DISMISS_JOIN_MODAL_STORAGE_KEY, new Date().toISOString())
    if (joinConfirmed) {
      dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    }
  }, [dispatch, handleClearEnrolment, showModal, activateMembershipCta, maybeLaterCta])

  useEffect(() => {
    if (shouldShowModal) showJoinModal()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowModal])
}

export default useLoyaltyEnrolExistingUserModal
